/**
 * Scrolls to a section by ID, with retry logic for lazy-loaded content.
 * Polls for the element to appear in the DOM before scrolling.
 * 
 * @param sectionId - The ID of the element to scroll to (without #)
 * @param options - Configuration options
 */
export const scrollToSection = (
  sectionId: string,
  options: {
    maxAttempts?: number;
    pollInterval?: number;
    scrollBehavior?: ScrollBehavior;
    block?: ScrollLogicalPosition;
  } = {}
): void => {
  const {
    maxAttempts = 20,
    pollInterval = 50,
    scrollBehavior = "smooth",
    block = "start",
  } = options;

  let attempts = 0;

  const doScroll = (el: HTMLElement) => {
    el.scrollIntoView({ behavior: scrollBehavior, block });
  };

  const tryScroll = () => {
    const element = document.getElementById(sectionId);

    if (element) {
      doScroll(element);
      // Re-scroll multiple times to compensate for lazy-loaded sections
      // pushing content as they hydrate (Suspense fallbacks → real components).
      [150, 350, 700, 1200, 1800, 2600].forEach((delay) => {
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          if (el) doScroll(el);
        }, delay);
      });
      return;
    }

    attempts++;
    
    if (attempts < maxAttempts) {
      setTimeout(tryScroll, pollInterval);
    }
  };

  tryScroll();
};

/**
 * Click handler for anchor links that use the scroll utility.
 * Prevents default browser behavior and uses JS-based scrolling.
 */
export const handleAnchorClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string
): void => {
  if (href.startsWith("#")) {
    e.preventDefault();
    const sectionId = href.slice(1);
    scrollToSection(sectionId);
  }
};
